import { CacheStrategy } from '@rammetec/data-access';
import { AUDIT_FIELD_DESCRIPTORS, AUDIT_FOREIGN_KEYS } from './_auditFields.js';
import { BUSINESS_ALLOWED_DATA_SOURCES, BUSINESS_DEFAULT_DATA_SOURCE } from './dataSourcePolicy.js';
import { PRODUCTION_STOP } from '../types/production-stop.js';
import { USER_DATA } from '../types/user.js';
import { FIELD_LIMITS } from '../validation/fieldLimits.js';
/**
 * Descriptor for ProductionStop (org/{orgId}/production-stop).
 *
 * Top-level transactional collection. No lifecycle fields; open ⇔ `endedAt`
 * is null (no `status`). `stepId` is a plain pointer (subcollection).
 */
export const productionStopDescriptor = {
    name: PRODUCTION_STOP,
    label: 'Parada de produção',
    domain: 'production',
    collectionPath: 'org/{orgId}/production-stop',
    pathPatternKeys: {
        orgId: 'string',
    },
    version: 1,
    description: 'Production stop (parada) — named idle interval, hybrid run/machine anchor (spec 0041 §2.4)',
    repositoryConfig: {
        dataSource: BUSINESS_DEFAULT_DATA_SOURCE,
        cache: CacheStrategy.InMemory,
    },
    allowedDataSources: BUSINESS_ALLOWED_DATA_SOURCES,
    fields: [
        {
            name: 'id',
            label: 'ID',
            type: 'string',
            required: true,
            description: 'Unique production stop identifier',
        },
        {
            name: 'runId',
            label: 'Execução',
            type: 'string',
            required: false,
            description: 'Anchor A — stop born from a production run',
        },
        {
            name: 'machineId',
            label: 'Máquina',
            type: 'string',
            required: false,
            description: 'Anchor B — required when there is no run; copied from the run snapshot otherwise',
        },
        {
            name: 'stopReasonId',
            label: 'Motivo',
            type: 'string',
            required: false,
            description: 'Stop reason; required when org param requireStopReason is on',
        },
        {
            name: 'productionOrderId',
            label: 'Ordem de produção',
            type: 'string',
            required: false,
            description: 'Immutable snapshot of the run (null on a machine-only stop)',
        },
        {
            name: 'ticketId',
            label: 'Talão',
            type: 'string',
            required: false,
            description: 'Immutable snapshot of the run',
        },
        {
            name: 'stepId',
            label: 'Etapa',
            type: 'string',
            required: false,
            description: 'Plain pointer to ProductionOrderStep (subcollection — no FK; resolved with OP context)',
        },
        {
            name: 'processId',
            label: 'Processo',
            type: 'string',
            required: false,
            description: 'Immutable snapshot of the run',
        },
        {
            name: 'factoryId',
            label: 'Fábrica',
            type: 'string',
            required: false,
            description: 'From the run, or the factory cascade when the stop is machine-only',
        },
        {
            name: 'operatorUserId',
            label: 'Operador',
            type: 'string',
            required: false,
            description: 'From the run, or the input (empty = who registered)',
        },
        {
            name: 'startedAt',
            label: 'Início',
            type: 'date',
            required: true,
            description: 'Start of the idle interval (default now; backdating allowed)',
        },
        {
            name: 'endedAt',
            label: 'Fim',
            type: 'date',
            required: false,
            description: 'End of the idle interval (null while open)',
        },
        {
            name: 'durationMs',
            label: 'Duração (ms)',
            type: 'number',
            required: false,
            description: 'Gross duration in ms (endedAt − startedAt). Measured shop-floor time — not capped by MAX_DURATION_MS. Does not subtract from the run.',
        },
        {
            name: 'notes',
            label: 'Observações',
            type: 'string',
            required: false,
            maxLength: FIELD_LIMITS.NOTES,
        },
        ...AUDIT_FIELD_DESCRIPTORS,
    ],
    foreignKeys: [
        ...AUDIT_FOREIGN_KEYS,
        {
            fieldName: 'runId',
            targetEntity: 'ProductionRun',
            relationship: 'many-to-one',
            displayField: 'id',
            description: 'Execução',
        },
        {
            fieldName: 'machineId',
            targetEntity: 'Machine',
            relationship: 'many-to-one',
            displayField: 'description',
            description: 'Máquina',
        },
        {
            fieldName: 'stopReasonId',
            targetEntity: 'ProductionStopReason',
            relationship: 'many-to-one',
            displayField: 'description',
            description: 'Motivo de parada',
        },
        {
            fieldName: 'productionOrderId',
            targetEntity: 'ProductionOrder',
            relationship: 'many-to-one',
            displayField: 'code',
            description: 'Ordem de produção (snapshot do run)',
        },
        {
            fieldName: 'ticketId',
            targetEntity: 'ProductionTicket',
            relationship: 'many-to-one',
            displayField: 'ticketNumber',
            description: 'Talão (snapshot do run)',
        },
        {
            fieldName: 'processId',
            targetEntity: 'Process',
            relationship: 'many-to-one',
            displayField: 'description',
            description: 'Processo (snapshot do run)',
        },
        {
            fieldName: 'factoryId',
            targetEntity: 'Factory',
            relationship: 'many-to-one',
            displayField: 'description',
            description: 'Fábrica',
        },
        {
            fieldName: 'operatorUserId',
            targetEntity: USER_DATA,
            relationship: 'many-to-one',
            displayField: 'name',
            expand: false,
            description: 'Operador',
        },
    ],
};
//# sourceMappingURL=production-stop.descriptor.js.map